import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Sparkles, Loader2, HeartPulse, ShieldAlert, Replace, AlertTriangle, Activity, Search, RefreshCw, Users, Bot } from 'lucide-react';
import EkipmanZekasiDetay, { SkorRozeti, RISK_META } from './EkipmanZekasiDetay';
import EkipmanZekasiSohbet from './EkipmanZekasiSohbet';
import { EkipmanZekasiEksiklikler, EkipmanZekasiTarama } from './EkipmanZekasiPaneller';
import EkipmanZekasiMaliyet from './EkipmanZekasiMaliyet';
import { useAuth } from '../../contexts/AuthContext';
import { PdfButton } from './PdfButton';
import { exportFiloPdf } from '../../utils/ekipmanZekasiPdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIP_LABELS = {
  asansor: 'Asansör', jenerator: 'Jeneratör', ups: 'UPS', trafo: 'Trafo', yangin_algilama: 'Yangın Algılama', sprinkler: 'Sprinkler',
  duman_tahliye: 'Duman Tahliye', hvac_klima: 'HVAC / Klima', buhar_kazani: 'Buhar Kazanı', lpg_tanki: 'LPG Tankı', forklift: 'Forklift',
  yapi_iskelesi: 'Yapı İskelesi', yuruyen_merdiven: 'Yürüyen Merdiven', topraklama: 'Topraklama', paratoner: 'Paratoner', katodik_koruma: 'Katodik Koruma',
  su_deposu: 'Su Deposu', kompanzasyon: 'Kompanzasyon', boyler: 'Boyler', genlesme_tanki: 'Genleşme Tankı', kazan: 'Kazan', brulor: 'Brülör',
  kum_filtresi: 'Kum Filtresi', transpalet: 'Transpalet', erisim_platformu: 'Erişim Platformu', diger: 'Diğer',
};
const AKSIYON = { izle: 'İzle', bakim_planla: 'Bakım Planla', onar: 'Onar', degistir: 'Değiştir', acil_mudahale: 'Acil Müdahale' };

const KpiKart = ({ icon: Icon, label, value, sub, tone = 'text-white', testId }) => (
  <Card className="bg-[#1A2332] border-gray-800" data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-gray-400 uppercase tracking-wider">{label}</p>
        <Icon className={`w-4 h-4 ${tone}`} />
      </div>
      <p className={`text-2xl font-bold mt-1 ${tone}`}>{value ?? '—'}</p>
      {sub && <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const TABS = [
  { id: 'filo', label: 'Filo' }, { id: 'sohbet', label: 'Uzman Asistan' }, { id: 'eksiklikler', label: 'Kapanmamış Eksiklikler' }, { id: 'maliyet', label: 'Maliyet Öngörüsü' }, { id: 'tarama', label: 'Filo Taraması' },
];

export default function PKEkipmanZekasi() {
  const { user } = useAuth();
  const [tab, setTab] = useState('filo');
  const [kpi, setKpi] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [risk, setRisk] = useState('tumu');
  const [tip, setTip] = useState('tumu');
  const [tesis, setTesis] = useState('tumu');
  const [tesisTurleri, setTesisTurleri] = useState([]);
  const [onlyReplace, setOnlyReplace] = useState(false);
  const [analyzed, setAnalyzed] = useState('tumu');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [fleetAlerts, setFleetAlerts] = useState([]);
  const pageSize = 25;

  const buildParams = useCallback((extra) => {
    const params = { ...extra };
    if (risk !== 'tumu') params.risk = risk;
    if (tip !== 'tumu') params.tip = tip;
    if (tesis !== 'tumu') params.tesis_turu = tesis;
    if (onlyReplace) params.only_replace = 1;
    if (analyzed !== 'tumu') params.analyzed = analyzed;
    if (search.trim()) params.search = search.trim();
    return params;
  }, [risk, tip, tesis, onlyReplace, analyzed, search]);

  useEffect(() => {
    axios.get(`${API}/ai/chat/scope-options`, { withCredentials: true })
      .then(r => setTesisTurleri(r.data?.tesis_turleri || []))
      .catch(() => setTesisTurleri([]));
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams({ page, page_size: pageSize });
      const [k, f, a] = await Promise.all([
        axios.get(`${API}/ai/kpi`, { withCredentials: true }),
        axios.get(`${API}/ai/fleet`, { params, withCredentials: true }),
        axios.get(`${API}/ai/fleet-alerts`, { withCredentials: true }),
      ]);
      setKpi(k.data); setItems(f.data.items); setTotal(f.data.total); setFleetAlerts(a.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ekipman Zekâsı verileri yüklenemedi');
    } finally { setLoading(false); }
  }, [page, buildParams]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const analyze = async (item) => {
    setAnalyzingId(item.id);
    try {
      const res = await axios.post(`${API}/ai/analyze/${item.id}?force=true`, {}, { withCredentials: true });
      toast.success(`${res.data.agent_name}: sağlık ${res.data.health_score}/100 (${RISK_META[res.data.risk_level]?.label})`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analiz başarısız');
    } finally { setAnalyzingId(null); }
  };

  const tipler = Object.keys(TIP_LABELS);

  const filoPdf = async (pageSize) => {
    const params = buildParams({ page: 1, page_size: 2000 });
    const r = await axios.get(`${API}/ai/fleet`, { params, withCredentials: true });
    const ozet = [
      risk !== 'tumu' && `Risk: ${RISK_META[risk]?.label}`, tip !== 'tumu' && `Tip: ${TIP_LABELS[tip]}`,
      tesis !== 'tumu' && `Tesis: ${tesis === 'bos' ? 'Tesis türü girilmemiş' : (tesisTurleri.find(t => t.key === tesis)?.label || tesis)}`, onlyReplace && 'Sadece değişim önerilenler',
      analyzed === 'yes' && 'Sadece analizli', analyzed === 'no' && 'Sadece analizsiz', search.trim() && `"${search.trim()}"`,
    ].filter(Boolean).join(' · ') || 'Tüm filo';
    exportFiloPdf({ items: r.data.items, kpi, fleetAlerts, filtreOzeti: ozet, pageSize, tipLabels: TIP_LABELS });
  };

  return (
    <div className="space-y-5" data-testid="pk-ekipman-zekasi">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#E67E22]" /> Ekipman Zekâsı</h2>
          <p className="text-xs text-gray-400 mt-1">9 tip uzmanı AI ajanı + deterministik risk motoru · Manuel analiz ve filo taraması (kişisel API anahtarınızla)</p>
        </div>
        <div className="text-right text-[11px] text-gray-500">
          {kpi && <>Bu ay <b className="text-gray-300">{kpi.bu_ay_analiz}</b>/{kpi.aylik_limit} analiz · ${kpi.bu_ay_maliyet_usd}</>}
          <Button variant="ghost" size="sm" onClick={fetchAll} className="ml-2 h-7 text-gray-400 hover:text-white" data-testid="ez-yenile"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiKart icon={HeartPulse} label="Filo Sağlık Ort." value={kpi?.filo_saglik_ort ?? '—'} sub={kpi ? `${kpi.analiz_edilen}/${kpi.toplam_ekipman} ekipman analizli` : ''} tone={kpi?.filo_saglik_ort == null ? 'text-gray-300' : kpi.filo_saglik_ort >= 75 ? 'text-emerald-400' : kpi.filo_saglik_ort >= 50 ? 'text-yellow-400' : 'text-red-400'} testId="kpi-saglik" />
        <KpiKart icon={AlertTriangle} label="Kritik Ekipman" value={kpi?.kritik} sub={kpi ? `${kpi.riskli} riskli` : ''} tone="text-red-400" testId="kpi-kritik" />
        <KpiKart icon={Replace} label="Değişim Önerilen" value={kpi?.degisim_onerilen} tone="text-orange-400" testId="kpi-degisim" />
        <KpiKart icon={ShieldAlert} label="Açık Güvenlik Eksikliği" value={kpi?.acik_guvenlik_eksikligi} sub="kritik/yüksek güvenlik bulgusu" tone="text-amber-400" testId="kpi-guvenlik" />
        <KpiKart icon={Activity} label="Bu Ay Analiz" value={kpi?.bu_ay_analiz} sub={kpi ? `${kpi.tokens_in + kpi.tokens_out} token` : ''} tone="text-cyan-400" testId="kpi-analiz" />
      </div>

      <div className="flex gap-1 border-b border-gray-800 overflow-x-auto" data-testid="ez-tabs">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === t.id ? 'border-[#E67E22] text-white' : 'border-transparent text-gray-400 hover:text-gray-200'}`} data-testid={`ez-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'sohbet' && <EkipmanZekasiSohbet />}
      {tab === 'eksiklikler' && <EkipmanZekasiEksiklikler />}
      {tab === 'maliyet' && <EkipmanZekasiMaliyet onOpenEkipman={(e) => setSelected({ id: e.id, ekipman_tipi: e.tip, alt_tip: e.alt_tip, marka: e.marka, model: e.model, seri_no: e.seri_no, bina_adi: e.bina_adi })} />}
      {tab === 'tarama' && <EkipmanZekasiTarama isAdmin={!!user?.is_admin} />}

      {tab === 'filo' && fleetAlerts.length > 0 && (
        <div className="rounded-lg border border-purple-800/50 bg-purple-900/15 p-3 space-y-1" data-testid="filo-uyarilari">
          <p className="text-xs font-semibold text-purple-200 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Filo Deseni Uyarıları ({fleetAlerts.length})</p>
          {fleetAlerts.slice(0, 3).map(a => <p key={a.id} className="text-[11px] text-purple-100/90">• {a.mesaj}</p>)}
        </div>
      )}

      {tab === 'filo' && <Card className="bg-[#1A2332] border-gray-800">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-gray-500" />
              <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Seri no, marka, model…" className="bg-gray-800 border-gray-700 text-white h-8 pl-7 w-52 text-xs" data-testid="ez-search" />
            </div>
            <Select value={risk} onValueChange={v => { setRisk(v); setPage(1); }}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 w-36 text-xs" data-testid="ez-risk-filtre"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700 text-white">
                <SelectItem value="tumu">Tüm riskler</SelectItem>
                {Object.entries(RISK_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}{kpi?.dagilim ? ` (${kpi.dagilim[k]})` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={tip} onValueChange={v => { setTip(v); setPage(1); }}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 w-44 text-xs" data-testid="ez-tip-filtre"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-72">
                <SelectItem value="tumu">Tüm tipler</SelectItem>
                {tipler.map(t => <SelectItem key={t} value={t}>{TIP_LABELS[t]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={tesis} onValueChange={v => { setTesis(v); setPage(1); }}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 w-44 text-xs" data-testid="ez-tesis-filtre"><SelectValue placeholder="Tesis türü" /></SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-72">
                <SelectItem value="tumu">Tüm tesis türleri</SelectItem>
                {tesisTurleri.map(t => <SelectItem key={t.key} value={t.key} data-testid={`ez-tesis-opt-${t.key}`}>{t.label} ({t.bina_sayisi} bina)</SelectItem>)}
                <SelectItem value="bos" data-testid="ez-tesis-opt-bos">Tesis türü girilmemiş</SelectItem>
              </SelectContent>
            </Select>
            <Select value={analyzed} onValueChange={v => { setAnalyzed(v); setPage(1); }}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 w-40 text-xs" data-testid="ez-analiz-filtre"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700 text-white">
                <SelectItem value="tumu">Analizli + analizsiz</SelectItem>
                <SelectItem value="yes">Sadece analizli</SelectItem>
                <SelectItem value="no">Sadece analizsiz</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-1.5 text-xs text-gray-300 ml-1">
              <Switch checked={onlyReplace} onCheckedChange={v => { setOnlyReplace(v); setPage(1); }} className="scale-75 data-[state=checked]:bg-orange-600" data-testid="ez-degisim-toggle" />
              Sadece değişim önerilenler
            </label>
            <span className="ml-auto text-[11px] text-gray-500">{total} ekipman</span>
            <PdfButton onExport={filoPdf} testId="ez-filo-pdf" disabled={loading || total === 0} />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="ez-filo-tablo">
              <thead>
                <tr className="text-gray-400 border-b border-gray-700 text-left">
                  <th className="py-2 pr-3">Sağlık</th>
                  <th className="py-2 pr-3">Ekipman</th>
                  <th className="py-2 pr-3">Bina</th>
                  <th className="py-2 pr-3">Uzman Ajan</th>
                  <th className="py-2 pr-3">Özet / Öncelikli Aksiyon</th>
                  <th className="py-2 pr-3">Son Analiz</th>
                  <th className="py-2 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {loading && items.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Yükleniyor…</td></tr>}
                {!loading && items.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-gray-500">Filtreye uyan ekipman yok.</td></tr>}
                {items.map(it => (
                  <tr key={it.id} className="border-b border-gray-800 hover:bg-gray-800/40 cursor-pointer" onClick={() => setSelected(it)} data-testid={`ez-row-${it.id}`}>
                    <td className="py-2 pr-3"><SkorRozeti score={it.ai_health_score} level={it.ai_risk_level} /></td>
                    <td className="py-2 pr-3">
                      <p className="text-white font-medium">{it.alt_tip || TIP_LABELS[it.ekipman_tipi] || it.ekipman_tipi}</p>
                      <p className="text-gray-500 font-mono text-[10px]">{it.marka} {it.model} · {it.seri_no}</p>
                    </td>
                    <td className="py-2 pr-3 text-gray-300">{it.bina_adi || '-'}</td>
                    <td className="py-2 pr-3 text-gray-400 flex items-center gap-1"><Bot className="w-3 h-3 text-[#E67E22]" /> {it.agent_name}</td>
                    <td className="py-2 pr-3 max-w-md">
                      {it.summary_tr ? (
                        <>
                          <p className="text-gray-300 line-clamp-2">{it.summary_tr}</p>
                          <div className="flex gap-1 mt-1">
                            {it.top_action && <Badge className={`text-[9px] ${it.top_action === 'degistir' || it.top_action === 'acil_mudahale' ? 'bg-red-700' : 'bg-blue-800'} text-white`}>{AKSIYON[it.top_action]}</Badge>}
                            {it.kritik_bulgu > 0 && <Badge className="text-[9px] bg-red-900 text-red-200">{it.kritik_bulgu} kritik bulgu</Badge>}
                            {it.ai_replace_suggested && <Badge className="text-[9px] bg-orange-800 text-white">Değişim önerisi</Badge>}
                          </div>
                        </>
                      ) : <span className="text-gray-600">Henüz analiz edilmedi</span>}
                    </td>
                    <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">{it.ai_last_analyzed_at ? new Date(it.ai_last_analyzed_at).toLocaleDateString('tr-TR') : '-'}</td>
                    <td className="py-2 text-right" onClick={e => e.stopPropagation()}>
                      {!(user?.is_viewer && !user?.is_admin) && (
                      <Button size="sm" onClick={() => analyze(it)} disabled={!!analyzingId} title="Analiz Et"
                        className="h-7 px-2 bg-[#E67E22]/20 hover:bg-[#E67E22]/40 text-[#E67E22] border border-[#E67E22]/40" data-testid={`ez-analiz-${it.id}`}>
                        {analyzingId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > pageSize && (
            <div className="flex items-center justify-end gap-2 text-xs text-gray-400">
              <Button variant="outline" size="sm" className="h-7 border-gray-700" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Önceki</Button>
              <span>{page} / {Math.ceil(total / pageSize)}</span>
              <Button variant="outline" size="sm" className="h-7 border-gray-700" disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}>Sonraki</Button>
            </div>
          )}
        </CardContent>
      </Card>}

      <EkipmanZekasiDetay ekipman={selected} open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }} onAnalyzed={fetchAll} />
    </div>
  );
}
